import { useEffect, useRef, useState } from 'react';

/**
 * targetTimestamp(만료 시점, unix ms)까지 남은 시간을 초 단위로 반환한다.
 * 0초가 되면 onExpire 콜백을 1회만 호출한다.
 *
 * - targetTimestamp가 null이면 hook이 비활성화된다 (interval 시작 안 함).
 * - 화면 이탈 후 복귀 시 즉시 다시 계산되도록 1초 interval을 사용한다.
 *
 * onExpire 는 ref 로 보관하여 변경 시 effect 재실행을 트리거하지 않는다.
 * 호출자는 안정된 함수를 넘기지 않아도 안전하다.
 */
export function useCountdown(
  targetTimestamp: number | null,
  onExpire?: () => void
): number | null {
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(() => {
    if (!targetTimestamp) return null;
    return Math.max(0, Math.floor((targetTimestamp - Date.now()) / 1000));
  });

  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!targetTimestamp) {
      setRemainingSeconds(null);
      return undefined;
    }

    const compute = () => Math.max(0, Math.floor((targetTimestamp - Date.now()) / 1000));

    // 마운트/타깃 변경 시점에 즉시 동기화
    setRemainingSeconds(compute());

    let fired = false;
    const interval = window.setInterval(() => {
      const next = compute();
      setRemainingSeconds(next);

      if (next <= 0 && !fired) {
        fired = true;
        if (onExpireRef.current) onExpireRef.current();
        window.clearInterval(interval);
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [targetTimestamp]);

  return remainingSeconds;
}

/**
 * 초를 `mm:ss` 형식으로 포맷한다.
 *  - 3600초 이상은 `hh:mm:ss` 가 아닌 분으로 표시 (`120:05`) — 학교 발급 코드는 짧은 만료시간이므로 충분.
 *  - 60초 미만은 한 자리 분 + 초 (`0:45`)
 */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const remainingSeconds = safe % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}
